import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';

declare global {
  interface Window {
    __loadAnalytics?: () => void;
    __analyticsLoaded?: boolean;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const STORAGE_KEY = 'cookie_consent';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (accepted) return;
    const declinedThisSession = sessionStorage.getItem(STORAGE_KEY);
    if (declinedThisSession) return;
    const timer = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(timer);
  }, []);

  function logChoice(choice: 'accepted_all' | 'analytics_only' | 'declined') {
    supabase.from('cookie_consent_events').insert({ choice }).then(() => {});
  }

  function acceptAll() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ analytics: true, marketing: true }));
    setVisible(false);
    logChoice('accepted_all');
    window.__loadAnalytics?.();
    setTimeout(() => {
      window.gtag?.('event', 'page_view', {
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
      });
    }, 100);
  }

  function savePreferences() {
    const choice = analyticsEnabled ? 'analytics_only' : 'declined';
    if (analyticsEnabled) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ analytics: analyticsEnabled, marketing: marketingEnabled }));
    } else {
      sessionStorage.setItem(STORAGE_KEY, 'declined');
    }
    setVisible(false);
    logChoice(choice);
    if (analyticsEnabled) {
      window.__loadAnalytics?.();
      setTimeout(() => {
        window.gtag?.('event', 'page_view', {
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      }, 100);
    }
  }

  if (!visible) return null;

  return (
    <div
      data-cookie-consent-banner
      className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-5 animate-slide-up"
      style={{ animation: 'cookieSlideUp 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
    >
      <style>{`
        @keyframes cookieSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center mt-0.5">
              <Shield className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] text-gray-800 leading-relaxed font-medium">
                Your privacy matters to us
              </p>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                We use cookies to keep things running smoothly and improve the tools you use. No personal data is sold — ever.
              </p>
            </div>
          </div>

          {showPreferences && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              <label className="flex items-center justify-between cursor-pointer group">
                <div>
                  <span className="text-sm font-medium text-gray-700">Essential</span>
                  <span className="text-xs text-gray-400 ml-2">Always on</span>
                </div>
                <div className="w-9 h-5 bg-emerald-500 rounded-full relative pointer-events-none">
                  <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-white rounded-full shadow-sm" />
                </div>
              </label>

              <label className="flex items-center justify-between cursor-pointer group" onClick={() => setAnalyticsEnabled(!analyticsEnabled)}>
                <div>
                  <span className="text-sm font-medium text-gray-700">Analytics & Performance</span>
                  <p className="text-xs text-gray-400 mt-0.5">Helps us understand how you use the app so we can make it better</p>
                </div>
                <div className={`w-9 h-5 rounded-full relative transition-colors ${analyticsEnabled ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${analyticsEnabled ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </label>

              <label className="flex items-center justify-between cursor-pointer group" onClick={() => setMarketingEnabled(!marketingEnabled)}>
                <div>
                  <span className="text-sm font-medium text-gray-700">Marketing</span>
                  <p className="text-xs text-gray-400 mt-0.5">Personalizes recommendations and offers</p>
                </div>
                <div className={`w-9 h-5 rounded-full relative transition-colors ${marketingEnabled ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${marketingEnabled ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </label>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            {showPreferences ? (
              <button
                onClick={savePreferences}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
              >
                Save preferences
              </button>
            ) : (
              <>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
                >
                  Got it
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-3 py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Manage
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
